import { Authenticator, TextField, useAuthenticator } from '@aws-amplify/ui-react';
import type { AuthenticatorProps } from '@aws-amplify/ui-react';
// Unlayered Legacy styles ------------<
import '@fontsource/lato';
import { Button, CssBaseline, ThemeProvider } from '@mui/material';
import { css } from '@styled/css';
import { ReactFlowProvider } from '@xyflow/react';
import { CognitoUser, ISignUpResult } from 'amazon-cognito-identity-js';
import { Auth, Hub, I18n } from 'aws-amplify';
import IPadZoomController from 'iPadZoomController';
import { LDProvider } from 'launchdarkly-react-client-sdk';
import { Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { RecoilRoot } from 'recoil';

import '@fontsource/lato/300-italic.css';
import '@fontsource/lato/300.css';
import '@fontsource/lato/400-italic.css';
import '@fontsource/lato/400.css';
import '@fontsource/lato/700-italic.css';
import '@fontsource/lato/700.css';
// Unlayered Legacy styles ------------>
import 'App.scss';
import 'styles/AgGridOverrides.scss';
import 'styles/CanvasCursors.scss';
import 'styles/DatePickersCommon.scss';
import 'styles/Dialogs.scss';
import 'styles/Fonts.scss';
import 'styles/LightTheme.css';
import 'styles/ReactFlowEdges.scss';
import 'styles/ReactFlowOverrides.scss';

import logo from 'assets/images/planera-logo.png';
import { handleAcceptInvite } from 'components/Auth/auth-utils';
import MagicLinkUserSetup from 'components/Auth/MagicLinkUserSetup/MagicLinkUserSetup';
import AfterSignUp from 'components/Auth/SignUp/AfterSignUp/AfterSignUp';
import SSOLogin from 'components/Auth/SSOLogin/SSOLogin';
import { FullPageLoadingHost } from 'components/Loading/FullPageLoadingHost';
import { useFullPageLoader } from 'components/Loading/useFullPageLoader';
import { ShortcutsProvider } from 'components/shortcuts/ShortcutsProvider';
import RefreshAppToast from 'components/StatefulApp/RefreshAppToast/RefreshAppToast';
import { showErrorToast } from 'components/Toast/Toast';
import { lazyWithRetry } from 'helpers/lazyWithRetry';
import { ERR_CANNOT_RESET_PASSWORD_FOR_SSO_EMAIL, ERR_USER_NOT_FOUND_FOR_FORGOT_PASSWORD, INVALID_PASSWORD_ERROR } from 'helpers/messages';
import { RumComponentContextProvider } from 'helpers/rum';
import { initOAuthCallbackHandler, isInIframe, signInWithPopup, SSO_ERROR_MESSAGES } from 'helpers/ssoPopupHelper';
import { extractDomainFromEmail, hideCustomErrorFromAmplifyAuth, showCustomErrorOnAmplifyAuth, unknownToError } from 'helpers/utils';
import { AUTOMATION_IDS } from 'state_management/automationIds';
import { AMPLIFY_AUTH_ERROR_KEY, CUSTOM_AMPLIFY_ERROR_ID, MAGIC_LOGIN_PATH, NEED_PASSWORD_SETUP_ATTRIBUTE_KEY, PROCORE_COMPANY_ID_KEY, PROCORE_COMPANY_ID_PARAM_KEY } from 'state_management/threadedConstants';
import { useCrossTabAuthReload } from 'state_management/useCrossTabAuthReload';

import { lightTheme } from './theme';

const StatefulApp = lazyWithRetry(() => import('components/StatefulApp/StatefulApp'));

// Initialize OAuth callback handler immediately when module loads
// This ensures it runs before any React component lifecycle
initOAuthCallbackHandler();

const listener = async (data: any) => {
  hideCustomErrorFromAmplifyAuth();
  switch (data.payload.event) {
    case 'signIn':
      try {
        // Get user's AuthUserID
        const authUserID = data.payload?.data?.username;
        const needPasswordSetup = data.payload?.data?.attributes?.[NEED_PASSWORD_SETUP_ATTRIBUTE_KEY] === 'false';
        if (!needPasswordSetup) await handleAcceptInvite(authUserID);
      } catch (error) {
        logger.error('An error occurred while trying to get/accept invites', {
          error,
          rum: {
            context: {
              feature: 'auth',
              action: 'accept-invite-on-signin'
            }
          }
        });
      }
      break;
    case 'signOut':
      logger.info('User signed out');
      window.history.pushState(undefined, '', '/');
      break;
    case 'completeNewPassword_failure':
    case 'forgotPasswordSubmit_failure':
      logger.warn('Error submitting password', { extra: data?.payload });

      if (data?.payload?.data?.code === 'InvalidPasswordException' || data?.payload?.data?.code === 'InvalidParameterException') {
        showCustomErrorOnAmplifyAuth(INVALID_PASSWORD_ERROR);
      }
      break;
  }
};

Hub.listen('auth', (data) => {
  void listener(data);
});

I18n.putVocabulariesForLanguage('en', {
  'Change Password': 'Done',
  'Confirm TOTP Code': 'Enter the 6-digits from your Authenticator app',
  Code: '123456',
  Confirm: 'Verify'
});

const FORCE_NEW_PASSWORD_ROUTE = 'forceNewPassword';

const PASSWORD_RESET_REQUIRED_EXCEPTION = 'PasswordResetRequiredException';
const INVALID_PARAMETER_EXCEPTION = 'InvalidParameterException';
const USER_NOT_FOUND_EXCEPTION = 'UserNotFoundException';

type SignInError = Error & { code: typeof PASSWORD_RESET_REQUIRED_EXCEPTION };
type ForgotPasswordError = Error & { code: typeof INVALID_PARAMETER_EXCEPTION | typeof USER_NOT_FOUND_EXCEPTION };

function App() {
  useCrossTabAuthReload();

  const { route } = useAuthenticator((context) => [context.route]);
  const { user } = useAuthenticator((context) => [context.user]);
  const [showAfterSignUp, setShowAfterSignUp] = useState(false);
  const queryParams = new URLSearchParams(window.location.search);
  const hash = new URLSearchParams(window.location.hash);
  const ssoQueryParam = queryParams.get('sso');
  const authErrorDescription = hash.get(AMPLIFY_AUTH_ERROR_KEY);
  const ssoErrorMessage = useRef('');
  if (authErrorDescription) {
    ssoErrorMessage.current = authErrorDescription;
  }
  const [showSSOLogin, setShowSSOLogin] = useState(!!(ssoQueryParam || ssoErrorMessage.current));
  const [showOrganizationNameInput, setShowOrganizationNameInput] = useState(false);
  const [isMagicLinkLoading, setIsMagicLinkLoading] = useState(false);
  const [isSSOPopupLoading, setIsSSOPopupLoading] = useState(false);
  const magicLinkProcessedRef = useRef(false);

  useFullPageLoader({
    id: 'app-auth-loading',
    show: isMagicLinkLoading || isSSOPopupLoading,
    message: isSSOPopupLoading ? 'Opening SSO login...' : 'Activating your account...',
    priority: 'appBootstrap',
    scope: 'app'
  });

  const needPasswordSetupValue = user?.attributes?.[NEED_PASSWORD_SETUP_ATTRIBUTE_KEY];
  const needPasswordSetup = needPasswordSetupValue === 'false';
  const formFieldsWithoutOrganizationName: AuthenticatorProps['formFields'] = {
    forceNewPassword: {
      name: {
        placeholder: 'Your Name',
        isRequired: true,
        order: 2
      },
      password: {
        placeholder: 'Set New Password',
        isRequired: true,
        order: 3
      },
      confirm_password: {
        placeholder: 'Set Confirm Password',
        isRequired: true,
        order: 4
      }
    }
  };

  const handleMagicLinkSignIn = useCallback(async (email: string, token: string) => {
    setIsMagicLinkLoading(true);
    try {
      // If already authenticated, clean URL and exit
      try {
        await Auth.currentAuthenticatedUser();
        window.history.replaceState(undefined, '', '/');
        return;
      } catch {
        // Not signed in; continue with magic-link flow
      }

      // Sign in using the magic link
      const response = await Auth.signIn({ username: email, password: '' });
      await Auth.sendCustomChallengeAnswer(response, token);
      // Remove the magic-login and query parameters from the URL
      window.history.replaceState(undefined, '', '/');
      window.location.reload();
    } catch (error) {
      logger.error('An error occurred while trying login via magic link', {
        error,
        rum: {
          context: {
            feature: 'auth',
            action: 'magic-link-signin'
          }
        }
      });
      showErrorToast('Failed to sign in, please try again or contact support.');
    } finally {
      setIsMagicLinkLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!needPasswordSetup) return;
    // An issue was observed where an SSO user incorrectly landed on the account setup screen (MagicLinkUserSetup).
    // This log has been added to aid in diagnosing such issues if they occur again. [PRD-22872]
    logger.info(`Current user needs auth setup, custom:permPassword = ${needPasswordSetupValue}`);
  }, [needPasswordSetup, needPasswordSetupValue]);

  useEffect(() => {
    // Magic link sign-in handling - only run once
    if (magicLinkProcessedRef.current) return;
    if (!window.location.pathname.startsWith(`/${MAGIC_LOGIN_PATH}`)) return;

    const urlParams = new URLSearchParams(window.location.search);
    const email = urlParams.get('email');
    const token = urlParams.get('token');

    if (!email || !token) return;

    magicLinkProcessedRef.current = true;
    void handleMagicLinkSignIn(email, token);
  }, [handleMagicLinkSignIn]);

  const services = {
    async handleSignUp(formData: any): Promise<ISignUpResult> {
      const { username, password, attributes } = formData;
      let response: any;
      let error: unknown;
      try {
        response = await Auth.signUp({
          username,
          password,
          attributes
        });

        if (response && response.user) {
          setShowAfterSignUp(true);
        }
      } catch (e) {
        error = e;
      }

      return new Promise((resolve, reject) => {
        setTimeout(() => {
          if (response) {
            resolve(response);
          } else {
            reject(unknownToError(error, 'handleSignUp error'));
          }
        }, 0);
      });
    },
    async handleSignIn(formData: any, sso?: boolean): Promise<CognitoUser> {
      const { username, password, email } = formData;
      let response: any;
      let error: unknown;
      try {
        if (sso) {
          const domain = extractDomainFromEmail(email);

          // Check if we're in an iframe - if so, use popup flow
          if (isInIframe()) {
            setIsSSOPopupLoading(true);
            try {
              // Save Procore company id before iframe SSO login so that we don't lose it during redirects
              const queryParams = new URLSearchParams(window.location.search);
              const companyId = queryParams.get(PROCORE_COMPANY_ID_PARAM_KEY);
              if (companyId) {
                localStorage.setItem(PROCORE_COMPANY_ID_KEY, companyId);
              }
              await signInWithPopup(domain);
              // After successful popup auth, navigate to home page
              window.history.pushState(undefined, '', '/');
              window.location.reload();
              // Return a dummy response to satisfy the promise
              return new Promise(() => {
                // This will never resolve because we're reloading
              });
            } catch (popupError) {
              setIsSSOPopupLoading(false);
              setShowSSOLogin(false); // Return to main login screen
              logger.error('SSO popup authentication failed', {
                error: popupError,
                rum: {
                  context: {
                    feature: 'auth',
                    action: 'sso-popup-signin'
                  }
                }
              });

              const errorMsg = popupError instanceof Error ? popupError.message : SSO_ERROR_MESSAGES.FAILED;
              showErrorToast(errorMsg);

              // Don't throw - let user retry from the main screen
              return new Promise((_, reject) => {
                setTimeout(() => reject(new Error('SSO login cancelled')), 0);
              });
            }
          } else {
            // Regular redirect flow when not in iframe
            response = await Auth.federatedSignIn({ customProvider: domain });
          }
        } else {
          response = await Auth.signIn({
            username,
            password
          });
        }

        if (response?.challengeParam?.userAttributes['custom:UserType'] !== 'invited') {
          setShowOrganizationNameInput(true);
        }
      } catch (e) {
        error = e;

        // User needs to update his/her password if he/she gets PasswordResetRequiredException.
        // Amplify SDK doesn't send the code automatically that's why we are calling the forgot password API manually
        if ((e as SignInError).code === PASSWORD_RESET_REQUIRED_EXCEPTION) {
          await Auth.forgotPassword(username);
        }
      }

      return new Promise((resolve, reject) => {
        setTimeout(() => {
          if (response) {
            resolve(response);
          } else {
            reject(unknownToError(error, 'handleSignIn error'));
          }
        }, 0);
      });
    },
    async handleForgotPassword(email: string) {
      let response: any;
      let error: unknown;
      try {
        response = await Auth.forgotPassword(email);
      } catch (e) {
        switch ((e as ForgotPasswordError).code) {
          case INVALID_PARAMETER_EXCEPTION:
            error = new Error(ERR_CANNOT_RESET_PASSWORD_FOR_SSO_EMAIL);
            break;
          case USER_NOT_FOUND_EXCEPTION:
            error = new Error(ERR_USER_NOT_FOUND_FOR_FORGOT_PASSWORD);
            break;
          default:
            error = e;
        }
      }

      return new Promise((resolve, reject) => {
        setTimeout(() => {
          if (response) {
            resolve(response);
          } else {
            reject(unknownToError(error, 'handleForgotPassword error'));
          }
        }, 0);
      });
    }
  };

  const components = {
    Header() {
      return (
        <div className="logo-wrap">
          <img
            className="logo"
            alt="Planera logo"
            src={logo}
            data-testid={AUTOMATION_IDS.LOGIN.LOGO}
          />
          <div className="logoTitle">{route === FORCE_NEW_PASSWORD_ROUTE ? 'Complete your account setup' : 'Welcome to Planera'}</div>
        </div>
      );
    },
    Footer() {
      return (
        <>
          <div
            className="customErrorInFooter"
            id={CUSTOM_AMPLIFY_ERROR_ID}
          />
          <div className="footer-wrap">
            <p>&copy; {new Date().getFullYear()} Planera.io All Rights Reserved</p>
          </div>
        </>
      );
    },
    SignIn: {
      Header() {
        return (
          <h2
            className={css({
              mt: 6,
              textAlign: 'center',
              fontSize: '20px',
              fontWeight: 700,
              minWidth: '498px'
            })}>
            Sign In
          </h2>
        );
      },
      Footer() {
        return (
          <>
            <div className="ssoLoginBtnWrap">
              <Button onClick={() => setShowSSOLogin(true)}>Use Single Sign-on</Button>
            </div>
            <div className="login-terms withLoginFooter">
              By signing in, you agree with Planera’s{' '}
              <a
                target="_blank"
                rel="noreferrer"
                href="https://www.planera.io/termsofuse"
                className="highlighted">
                Terms & Conditions
              </a>{' '}
              and{' '}
              <a
                target="_blank"
                rel="noreferrer"
                href="https://www.planera.io/privacypolicy"
                className="highlighted">
                Privacy Policy
              </a>
              .
            </div>
            <Authenticator.SignIn.Footer />
          </>
        );
      }
    },
    SignUp: {
      FormFields() {
        return (
          <div className="signupMessage">
            <Button
              sx={{ marginTop: '90px', fontWeight: 'bold' }}
              target="_blank"
              rel="noopener noreferrer"
              href="https://www.planera.io/book-demo">
              Book Demo to Access Planera
            </Button>
          </div>
        );
      }
    },
    ForceNewPassword: {
      FormFields() {
        return (
          <>
            {showOrganizationNameInput ? (
              <TextField
                label="Organization Name"
                spellCheck={true}
                name="custom:CompanyName"
                placeholder="Your Organization Name"
                required
              />
            ) : null}
            <Authenticator.ForceNewPassword.FormFields />
          </>
        );
      }
    }
  };

  const cancelSSOLogin = useCallback(() => {
    setShowSSOLogin(false);
    ssoErrorMessage.current = '';
  }, []);

  // Custom form fields with automation IDs
  const formFieldsWithAutomationIds = {
    ...formFieldsWithoutOrganizationName,
    signIn: {
      username: {
        ...formFieldsWithoutOrganizationName.signIn?.username,
        'data-testid': AUTOMATION_IDS.LOGIN.USERNAME_INPUT,
        labelHidden: true,
        isRequired: true
      },
      password: {
        ...formFieldsWithoutOrganizationName.signIn?.password,
        'data-testid': AUTOMATION_IDS.LOGIN.PASSWORD_INPUT,
        labelHidden: true,
        isRequired: true
      }
    },
    resetPassword: {
      username: {
        ...formFieldsWithoutOrganizationName.resetPassword?.username,
        labelHidden: true
      }
    },
    confirmResetPassword: {
      ...formFieldsWithoutOrganizationName.confirmResetPassword,
      confirmation_code: {
        ...formFieldsWithoutOrganizationName.confirmResetPassword?.confirmation_code,
        labelHidden: true
      },
      password: {
        ...formFieldsWithoutOrganizationName.confirmResetPassword?.password,
        labelHidden: true
      },
      confirm_password: {
        ...formFieldsWithoutOrganizationName.confirmResetPassword?.confirm_password,
        labelHidden: true
      }
    }
  };

  return (
    <RumComponentContextProvider componentName="app">
      {showSSOLogin ? (
        <SSOLogin
          onSignIn={(event) => void services.handleSignIn(event, true)}
          onCancel={cancelSSOLogin}
          overrideError={ssoErrorMessage.current}
        />
      ) : showAfterSignUp ? (
        <AfterSignUp />
      ) : needPasswordSetup ? (
        <MagicLinkUserSetup />
      ) : isMagicLinkLoading ? (
        <div
          className={css({
            display: 'flex',
            alignItems: 'start',
            justifyContent: 'center',
            h: '100vh',
            w: '100%',
            pt: '124px',
            bg: 'white'
          })}>
          <div
            className={css({
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '3xl'
            })}>
            <img
              src={logo}
              alt="Planera logo"
              className={css({ w: 'base', h: 'base' })}
              data-testid={AUTOMATION_IDS.LOGIN.LOGO}
            />
            <div
              className={css({
                fontStyle: 'normal',
                fontWeight: 'bold',
                fontSize: '20px',
                lineHeight: '28px',
                textAlign: 'center',
                color: 'black'
              })}>
              Welcome to Planera
            </div>
          </div>
        </div>
      ) : (
        <Authenticator
          hideSignUp
          components={components}
          formFields={formFieldsWithAutomationIds}
          services={services}
          loginMechanisms={['email']}
          signUpAttributes={['name']}>
          {({ user }) => (
            <LDProvider
              clientSideID={import.meta.env.VITE_LD_CLIENT_ID ?? '6275016546a60e15a96e8b6f'}
              context={{
                kind: 'user',
                key: (user as any)?.attributes?.sub,
                name: (user as any)?.attributes?.name,
                email: (user as any)?.attributes?.email
              }}
              options={{ evaluationReasons: true }}
              deferInitialization>
              <RecoilRoot>
                <ThemeProvider theme={lightTheme}>
                  <CssBaseline />
                  <ReactFlowProvider>
                    <Suspense fallback={null}>
                      <ShortcutsProvider>
                        <StatefulApp />
                      </ShortcutsProvider>
                      <RefreshAppToast />
                      <IPadZoomController />
                    </Suspense>
                  </ReactFlowProvider>
                </ThemeProvider>
              </RecoilRoot>
            </LDProvider>
          )}
        </Authenticator>
      )}
      <FullPageLoadingHost scope="app" />
    </RumComponentContextProvider>
  );
}

export default App;
